import * as Sentry from '@sentry/nextjs';
import {
  ddCaptureException,
  ddCaptureMessage,
  ddSetUser,
  ddSetContext,
  ddAddBreadcrumb,
} from './datadog';

/**
 * Unified telemetry seam — fans out to BOTH Sentry and Datadog with the same
 * signatures as the `Sentry.*` calls it replaces (MKT-247). Sentry stays fully
 * wired; Datadog runs in parallel and no-ops until its browser creds land.
 *
 * Call sites (error boundaries, the API layer, and the `useLogging` hook) import
 * from here instead of `@sentry/nextjs`. When Sentry is removed in the follow-up
 * ticket, delete the Sentry branch in each wrapper and no call site changes.
 */

/** Report an exception to Sentry and Datadog. Returns the Sentry event id. */
export const captureException = (
  ...args: Parameters<typeof Sentry.captureException>
): ReturnType<typeof Sentry.captureException> => {
  const [error, hint] = args;
  ddCaptureException(error, hint);
  return Sentry.captureException(...args);
};

/** Report a message to Sentry and Datadog. Returns the Sentry event id. */
export const captureMessage = (
  ...args: Parameters<typeof Sentry.captureMessage>
): ReturnType<typeof Sentry.captureMessage> => {
  const [message, hint] = args;
  ddCaptureMessage(String(message), hint);
  return Sentry.captureMessage(...args);
};

/** Associate a user with the Sentry scope and the Datadog RUM session. */
export const setUser = (
  ...args: Parameters<typeof Sentry.setUser>
): ReturnType<typeof Sentry.setUser> => {
  const [user] = args;
  if (user) ddSetUser(user as Parameters<typeof ddSetUser>[0]);
  return Sentry.setUser(...args);
};

/** Attach structured context to Sentry and Datadog. */
export const setContext = (
  ...args: Parameters<typeof Sentry.setContext>
): ReturnType<typeof Sentry.setContext> => {
  const [key, context] = args;
  ddSetContext(key, context);
  return Sentry.setContext(...args);
};

/** Record a breadcrumb on Sentry and its RUM-action equivalent on Datadog. */
export const addBreadcrumb = (
  ...args: Parameters<typeof Sentry.addBreadcrumb>
): ReturnType<typeof Sentry.addBreadcrumb> => {
  const [breadcrumb] = args;
  if (breadcrumb) ddAddBreadcrumb(breadcrumb);
  return Sentry.addBreadcrumb(...args);
};
